import React from "react";
import { Reveal } from "@/components/motion/Reveal";
import { ASSETS } from "@/data/content";
import { ArrowUpRight, Instagram, Phone } from "lucide-react";
import { Reviews } from "@/components/Reviews";
import { openBookingModal } from "@/components/BookingModal";
import { useI18n } from "@/i18n";

export default function Booking() {
  const { t, site } = useI18n();
  const b = t.booking;
  const EXTERNAL = [
    { label: "Booking.com", url: site.booking.booking },
    { label: "Airbnb", url: site.booking.airbnb },
  ];
  return (
    <section
      id="rezerwacja"
      data-testid="booking-section"
      className="relative bg-ink text-canvas py-14 md:py-20 overflow-hidden"
    >
      <img
        src={ASSETS.riverFromTerrace}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-15"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/90 to-ink" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 md:gap-16 items-start mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-6">
            <Reveal>
              <p className="overline text-canvas/60 mb-6">{b.overline}</p>
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-canvas">
                {b.headingA} <br />
                <span className="italic text-canvas/85">{b.headingB}</span>
              </h2>
            </Reveal>

            <Reveal>
              <div className="flex flex-wrap items-center gap-4 mt-12">
                <button
                  type="button"
                  onClick={openBookingModal}
                  data-testid="booking-cta-button"
                  className="inline-flex items-center gap-3 bg-brand-red hover:bg-brand-redDark text-canvas px-8 py-4 text-xs tracking-[0.3em] uppercase transition-colors duration-500"
                >
                  {b.cta}
                  <ArrowUpRight size={16} strokeWidth={1.2} />
                </button>
              </div>
            </Reveal>
          </div>

          <div className="col-span-12 md:col-span-6 md:pl-8">
            <Reveal delay={0.15}>
              <Reviews variant="dark" />
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-canvas/10 border border-canvas/10">
          <Reveal className="group bg-brand-red/70 hover:bg-brand-red/80 transition-colors duration-500">
            <a
              href={`tel:${site.contact.phone.replace(/\s/g, "")}`}
              data-testid="booking-direct-tile"
              className="block p-8 md:p-10 h-full"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="overline text-canvas/70 mb-3">{b.directLabel}</p>
                  <p className="serif text-2xl md:text-3xl font-light text-canvas">
                    {site.contact.phone}
                  </p>
                  <p className="mt-4 text-sm text-canvas/80 leading-relaxed max-w-xs [text-wrap:pretty]">
                    {b.directDesc}
                  </p>
                </div>
                <Phone
                  size={22}
                  strokeWidth={1.2}
                  className="shrink-0 text-canvas/70 group-hover:text-canvas group-hover:-translate-y-1 transition-all duration-500"
                />
              </div>
            </a>
          </Reveal>
          {EXTERNAL.map((e) => (
            <Reveal key={e.label} className="group bg-ink hover:bg-canvas/5 transition-colors duration-500">
              <a
                href={e.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`booking-link-${e.label.toLowerCase().replace(/\./g, "")}`}
                className="block p-8 md:p-10 h-full"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="overline text-canvas/50 mb-3">{b.reserveLabel}</p>
                    <p className="serif text-3xl md:text-4xl font-light text-canvas">
                      {e.label}
                    </p>
                  </div>
                  <ArrowUpRight
                    size={24}
                    strokeWidth={1}
                    className="text-canvas/50 group-hover:text-brand-red group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500"
                  />
                </div>
              </a>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16 flex flex-wrap items-center gap-8">
          <span className="overline text-canvas/60">{b.followUs}</span>
          <a
            href={site.socials.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="social-instagram"
            className="flex items-center gap-2 text-canvas/80 hover:text-brand-red transition-colors"
          >
            <Instagram size={18} strokeWidth={1.2} />
            <span className="text-sm tracking-wide">Instagram</span>
          </a>
        </Reveal>
      </div>
    </section>
  );
}
